import { supabase } from "./supabaseClient"; // Importar o cliente Supabase
import { axiosInstance } from "../config/axios"; // Importar configuração centralizada do Axios

export const productService = {
  async createProduct(productData) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para criar produtos."
        );
      }

      // Extrai initial_stock_quantity para enviar separadamente ou processar no backend
      const { initial_stock_quantity, ...restProductData } = productData;

      const response = await axiosInstance.post(
        `/products`,
        {
          ...restProductData,
          initial_stock_quantity, // Inclui o campo no payload
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.value;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || "Erro ao criar produto");
      }
      throw error;
    }
  },

  async getProducts(filters = {}) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar produtos."
        );
      }

      const response = await axiosInstance.get(`/products`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: filters,
      });
      if (filters.includeInactive) {
        return response.data;
      }
      return response.data.filter((product) => product.is_active !== false);
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar produtos"
        );
      }
      throw error;
    }
  },

  async getProductById(id) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar detalhes do produto."
        );
      }

      const response = await axiosInstance.get(`/products/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar detalhes do produto"
        );
      }
      throw error;
    }
  },

  async updateProduct(id, productData) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para atualizar produtos."
        );
      }

      const response = await axiosInstance.put(`/products/${id}`, productData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao atualizar produto"
        );
      }
      throw error;
    }
  },

  async deleteProduct(id, password) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para excluir produtos."
        );
      }

      const response = await axiosInstance.delete(`/products/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { password }, // Envia a senha no corpo da requisição DELETE
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao excluir produto"
        );
      }
      throw error;
    }
  },

  async getProductMovements(productId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar movimentações do produto."
        );
      }

      const response = await axiosInstance.get(
        `/products/${productId}/movements`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message ||
            "Erro ao buscar movimentações do produto"
        );
      }
      throw error;
    }
  },

  async getProductProductionDemand(productId, period = "this_year") {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar dados de produção e demanda."
        );
      }

      const response = await axiosInstance.get(
        `/products/${productId}/production-demand`,
        {
          params: { period },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message ||
            "Erro ao buscar dados de produção e demanda"
        );
      }
      throw error;
    }
  },

  async getProductLossVolume(productId, period = "this_year") {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar dados de perdas."
        );
      }

      const response = await axiosInstance.get(
        `/products/${productId}/loss-volume`,
        {
          params: { period },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar dados de perdas"
        );
      }
      throw error;
    }
  },

  async getProductStockMovement(productId, period = "7_days") {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar dados de stock e movimentação."
        );
      }

      const response = await axiosInstance.get(
        `/products/${productId}/stock-movement`,
        {
          params: { period },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message ||
            "Erro ao buscar dados de stock e movimentação"
        );
      }
      throw error;
    }
  },

  async getProductLotHistory(productId, limit = "10") {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar histórico de lotes."
        );
      }

      const response = await axiosInstance.get(
        `/products/${productId}/lot-history`,
        {
          params: { limit },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar histórico de lotes"
        );
      }
      throw error;
    }
  },

  async getProductAvailableLots(productId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar lotes disponíveis."
        );
      }

      const response = await axiosInstance.get(
        `/products/${productId}/available-lots`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar lotes disponíveis"
        );
      }
      throw error;
    }
  },

  async getProductClosedLots(productId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar lotes fechados."
        );
      }

      const response = await axiosInstance.get(
        `/products/${productId}/closed-lots`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar lotes fechados"
        );
      }
      throw error;
    }
  },

  async getProductLotMovements(productId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar movimentações dos lotes."
        );
      }

      const response = await axiosInstance.get(
        `/products/${productId}/lot-movements`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar movimentações dos lotes"
        );
      }
      throw error;
    }
  },

  // Buscar produtos ativos
  async getActiveProducts() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar produtos."
        );
      }

      const response = await axiosInstance.get(`/products`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // Filtrar apenas produtos ativos no frontend
      return response.data.filter(product => product.is_active !== false);
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar produtos ativos"
        );
      }
      throw error;
    }
  },

  // Criar variante de um produto
  async createVariant(parentProductId, variantData) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para criar variantes."
        );
      }

      const response = await axiosInstance.post(
        `/products/${parentProductId}/variants`,
        variantData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao criar variante"
        );
      }
      throw error;
    }
  },

  // Buscar variantes de um produto
  async getProductVariants(productId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar variantes."
        );
      }

      const response = await axiosInstance.get(
        `/products/${productId}/variants`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar variantes"
        );
      }
      throw error;
    }
  },

  // Buscar produtos que podem ser pais (não são variantes)
  async getAvailableParents() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar produtos."
        );
      }

      const response = await axiosInstance.get(`/products/available-parents/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar produtos disponíveis"
        );
      }
      throw error;
    }
  },

  // Buscar produtos compatíveis para transferência de stock
  async getTransferLinks(productId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar produtos compatíveis."
        );
      }

      const response = await axiosInstance.get(
        `/products/${productId}/transfer-links`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar produtos compatíveis"
        );
      }
      throw error;
    }
  },

  // Substituir a lista de produtos compatíveis para transferência de stock
  async updateTransferLinks(productId, linkedProductIds) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para atualizar produtos compatíveis."
        );
      }

      const response = await axiosInstance.put(
        `/products/${productId}/transfer-links`,
        { linked_product_ids: linkedProductIds },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao atualizar produtos compatíveis"
        );
      }
      throw error;
    }
  },

  // Buscar tipos de caixa já usados (sugestões para o cadastro de produto)
  async getBoxTypes() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar tipos de caixa."
        );
      }

      const response = await axiosInstance.get(`/products/box-types`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.box_types || [];
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar tipos de caixa"
        );
      }
      throw error;
    }
  },

  // Buscar produtos disponíveis para um cliente específico (por NIF)
  async getProductsByClientNif(clientNif) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error(
          "Usuário não autenticado. Faça login para buscar produtos."
        );
      }

      const response = await axiosInstance.get(
        `/products/by-client/${clientNif}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Erro ao buscar produtos para cliente"
        );
      }
      throw error;
    }
  },
};
